import { createContext, useReducer, useContext, useEffect } from "react";
import { useQryExpressFee } from "../../../api/tools";
import { useAsyncEffect } from "ahooks";
import ls from "../../../utils/ls";
import { calc, isEmptyObj, mediaMatch } from "../../../utils";
import { useShopInfo } from "../../../api/shop";
import {
  initialState,
  reducer,
  setAddress,
  setAddressList,
  setAddressListModalVisible,
  setBillData,
  setCoupon,
  setCouponList,
  setCouponListModalVisible,
  setExpressFee,
  setExpressType,
  setPayType,
  setShopInfo,
} from "./reducer";
import {
  useDelAddressInfo,
  useGetAddressByPassId,
} from "../../../api/UdianSysUserAddressInfo";
import { setAddressQuery, setFormVisible } from "../my/shippingAddress/reducer";
import { useQueryMyCouponForGoods } from "../../../api/couponReceive";
import cookies from "../../../utils/cookies";

/**
 * 根据省市区店铺|商品获取运费
 * @returns {{getExpressFee: (...params: any[]) => Promise<unknown>}}
 */

function ConfirmOrderController() {
  const isMdmax = mediaMatch.isMdmax;
  const { state, dispatch } = useConfirmOrderContext();
  const { getList } = useGetAddressByPassId();
  const { getCoupon } = useQueryMyCouponForGoods();
  const { getExpressFee } = useQryExpressFee(
    {},
    {
      onSuccess: (res) => {
        let { data } = res;

        if (data) {
          let fee = 0;
          data.forEach((item) => {
            fee += item.expressFee;
          });

          dispatch(setExpressFee(calc.add(fee)));
          dispatch(setBillData(getBillData(fee, state.coupon)));
        }
      },
    }
  );
  const { remove } = useDelAddressInfo();

  /**
   * 计算提交订单的总价格
   * @param fee
   * @returns {{goodsCount: number, totalPrice: number, goodsTotolPrice: number}}
   */
  const getBillData = (fee, coupon) => {
    let shopList = ls.getConfirmGoodsList();
    let totalPrice = 0;
    let goodsCount = 0;
    shopList.forEach((shop) => {
      shop.goodsList.forEach((item) => {
        totalPrice += item.price * item.quantity;
        goodsCount += item.quantity;
      });
    });

    let discountAmount = 0;

    if (!isEmptyObj(coupon)) {
      discountAmount =
        coupon.couponType === 2
          ? totalPrice * calc.add(1 - coupon.thresholdValue / 10)
          : coupon.thresholdValue;
    }

    return {
      totalPrice:
        state.expressType === 2
          ? calc.add(totalPrice + Number(fee) - discountAmount)
          : calc.add(totalPrice - discountAmount),
      goodsTotolPrice: calc.add(totalPrice),
      discountAmount,
      goodsCount,
    };
  };

  /**
   * 获取提交订单的参数
   * @returns {{userAddress: (*|string), amount: (number|*), consignee: (string|*), payType: string, shopList, transportFee, phone: (string|*), transportCosts, transportType: number, addressId}}
   */
  const getSubmitOrderQuery = () => {
    const {
      address,
      billData,
      payType,
      expressFee,
      expressType,
      shopInfo,
      coupon,
    } = state;
    let goodsShopList = ls.getConfirmGoodsList();

    let q = {
      addressId: address.id,
      amount: billData.totalPrice,
      consignee: address.linkor,
      payType: `${payType}`, // Stripe支付 = 1 , PayPal = 2 , 到店支付 = 3
      phone: address.mobileNo,
      transportCosts: expressType === 2 ? expressFee : 0,
      transportFee: expressType === 2 ? expressFee : 0,
      transportType: expressType, // 2 = 物流配送 ｜ 1 = 到店自提
      userAddress: address.areaName
        ? address.areaName.split(",").join("") + address.address
        : "",
      shopList: goodsShopList.map((item) => {
        return {
          remark: "",
          shopTransportCosts: 0,
          shopId: item.shopId,
          goodsList: item.goodsList,
        };
      }),
    };

    let shop_remark = document.getElementById("shop_remark");
    if (shop_remark && shop_remark.value) {
      q.shopList.forEach((shop) => {
        shop.remark = shop_remark.value;
      });
    }

    if (!isEmptyObj(coupon)) {
      q.useCouponAmount = coupon.couponAmount;
      q.couponId = coupon.id;

      q.shopList.forEach((_shop) => {
        _shop.goodsList.forEach((item) => {
          item.preferPrice = 0;
          if (coupon.couponItemList) {
            coupon.couponItemList.forEach((prod) => {
              if (
                prod.itemId === item.itemId &&
                prod.itemSkuId === item.itemSkuId
              ) {
                item.preferPrice = prod.couponAmount;
              }
            });
          }
        });
      });
    }

    if (expressType === 1 && !isEmptyObj(shopInfo)) {
      q.bookingShopId = shopInfo.shopId;
    }

    return q;
  };

  /**
   * 获取订单列表
   * @returns {Promise<void>}
   */
  const getAddressList = async () => {
    let { data } = await getList();

    if (data) {
      let currentAddress = data.find((res) => res.isDefault);
      if (currentAddress) {
        dispatch(setAddress(currentAddress));
        getExpressFee(currentAddress);
      }
      dispatch(setAddressList(data));
    }
  };

  /**
   * 获取优惠券列表
   * @returns {Promise<void>}
   */
  const getCouponList = async () => {
    let goodsList = ls.getConfirmGoodsList();
    let orderUsableItemList = [];
    goodsList.forEach((shop) => {
      shop.goodsList.forEach((goods) => {
        orderUsableItemList.push({
          shopId: shop.shopId,
          itemId: goods.itemId,
          itemSkuId: goods.itemSkuId,
          price: goods.price,
          quantity: goods.quantity,
          itemTotalAmount: calc.add(goods.price * goods.quantity),
        });
      });
    });

    let q = {
      // agentId: cookies.get('MALLagentId'),
      passId: cookies.getUserInfo().passId,
      orderUsableItemList,
    };

    let { data } = await getCoupon(q);
    if (data) {
      dispatch(setCouponList(data));
    }
  };

  /**
   * 删除收货地址
   * @param id
   * @returns {Promise<void>}
   */
  const removeAddress = async (id) => {
    let { data } = await remove({ id });
    if (data) {
      getAddressList();
    }
  };

  /**
   * 添加/编辑收货地址
   * @param item
   */
  const formVisibleHandle = (item = {}) => {
    dispatch(setAddressQuery(item));
    dispatch(setFormVisible(true));
  };

  /**
   * 打开可用优惠券列表
   * @param visible
   */
  const couponListVisibleHandle = (visible) => {
    if (state.couponList.length <= 0) return;
    dispatch(setCouponListModalVisible(visible));
  };

  /**
   * 关闭收货地址modal
   */
  const closeFormVisible = () => {
    dispatch(setFormVisible(false));
  };

  /**
   * 打开收货地址modal
   * @param state
   */
  const addressListModalVisibleHandle = (state) => {
    dispatch(setAddressListModalVisible(state));
  };

  /**
   * 选择支付方式
   * @param type
   */
  const selectPayType = (type) => {
    dispatch(setPayType(type));
  };

  /**
   * 选择收货地址
   * @param item
   */
  const selectAddress = (item) => {
    dispatch(setAddress(item));
    getExpressFee(item); // 选择地址后 重新获取，计算运费
    if (isMdmax) {
      dispatch(setAddressListModalVisible(false));
    }
  };

  /**
   * 选择收货方式
   * @param type
   */
  const selectExpressType = (type) => {
    dispatch(setExpressType(type));
    dispatch(setBillData(getBillData(state.expressFee, state.coupon)));
  };

  useEffect(() => {
    dispatch(setBillData(getBillData(state.expressFee, state.coupon)));
  }, [state.expressType]);

  /**
   * 选择店铺（到店自取）
   * @param shopInfo
   */
  const selectShopInfo = (shopInfo) => {
    dispatch(setShopInfo(shopInfo));
    getExpressFee(shopInfo); // 选择地址后 重新获取，计算运费
  };

  /**
   * 选择优惠券
   * @param coupon
   */
  const selectCoupon = (coupon) => {
    dispatch(setCoupon(coupon));
    dispatch(setCouponListModalVisible(false));
    dispatch(setBillData(getBillData(state.expressFee, coupon)));
  };

  /**
   * 移除优惠券
   */
  const removeCoupon = () => {
    dispatch(setCoupon({}));
    dispatch(setBillData(getBillData(state.expressFee, null)));
  };

  useEffect(() => {
    // console.log(getBillData(0, 0), 'getBillData(0, 0)');
    dispatch(setBillData(getBillData(0, {})));
    return () => {};
  }, []);

  return {
    selectShopInfo,
    selectExpressType,
    formVisibleHandle,
    closeFormVisible,
    selectAddress,
    selectPayType,
    selectCoupon,
    getAddressList,
    getCouponList,
    getSubmitOrderQuery,
    removeAddress,
    couponListVisibleHandle,
    addressListModalVisibleHandle,
    removeCoupon,
  };
}

/**
 * 提交订单页面模块context
 * @returns {{selectExpressType: selectExpressType, dispatch, selectPayType: selectPayType, getFee: (function(): {getExpressFee: (...params: any[]) => Promise<unknown>}), state, selectAddress: selectAddress}}
 */
function useConfirmOrderContext() {
  const { state, dispatch } = useContext(Context);

  return {
    state,
    dispatch,
  };
}

const Context = createContext(initialState);

export { useConfirmOrderContext, ConfirmOrderController };

function GetShopList({ children }) {
  const { dispatch } = useConfirmOrderContext();
  const { getShopList } = useShopInfo({
    shopType: 3,
    page: 1,
    limit: 100,
  });

  useAsyncEffect(async () => {
    let { data } = await getShopList();
    // console.log(data, 'shpLis');
    if (data.records) {
      dispatch({
        type: "setShopList",
        payload: data.records,
      });
    }
  }, []);

  return children;
}

export default function ConfirmOrderContext({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <Context.Provider value={{ state, dispatch }} reducer={reducer}>
      <GetShopList>{children}</GetShopList>
    </Context.Provider>
  );
}
